/**
 * Node context menu: bring-to-front / send-to-back within sibling groups, persisted
 * via Loro `updateNodeZIndex`. Uses the same stacking key rules as the rest of the canvas.
 */

import {
  compareCanvasSiblings,
  groupNodesByParentKey,
  nodeParentKey,
  nodeStackingZ,
} from "@/pages/canvas/utils/nodeStacking";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import { useCanvas } from "../context/CanvasContext";
import { isNodeLocked } from "../utils/nodeLock";
import { useCanvasPermissions } from "./useCanvasPermissions";

export function isZOrderEligibleForStacking(node: Node): boolean {
  return node.type !== "section" && node.type !== "chatSession";
}

/**
 * When using the node context menu on `anchorNodeId`, z-order applies to every
 * selected eligible node if the anchor is part of that selection; otherwise
 * only the anchor (e.g. multiple nodes selected but menu opened on another node).
 *
 * When the anchor is selected but ineligible (section, chat session), eligible
 * siblings in the same selection are still reordered (multi-select toolbar).
 */
export function zOrderTargetsForContextMenu(
  all: Node[],
  anchorNodeId: string,
): Node[] {
  const anchor = all.find((n) => n.id === anchorNodeId);
  if (!anchor) return [];

  const selectedEligible = all.filter(
    (n) => n.selected && isZOrderEligibleForStacking(n) && !isNodeLocked(n),
  );
  const anchorAmongSelectedEligible = selectedEligible.some(
    (n) => n.id === anchorNodeId,
  );
  if (anchorAmongSelectedEligible) {
    return selectedEligible;
  }

  if (!isZOrderEligibleForStacking(anchor)) {
    if (anchor.selected && selectedEligible.length > 0) {
      return selectedEligible;
    }
    return [];
  }

  return [anchor];
}

export function useCanvasZOrderActions() {
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow<Node>();
  const { updateNodeZIndex } = useCanvas();
  const handleNodeBringToFront = useCallback(
    (anchorNodeId: string) => {
      if (!canEdit) return;
      const all = reactFlowInstance.getNodes();
      const idSet = new Set(all.map((n) => n.id));
      const targets = zOrderTargetsForContextMenu(all, anchorNodeId);
      if (targets.length === 0) return;

      const byParent = groupNodesByParentKey(targets, idSet);
      for (const groupTargets of byParent.values()) {
        const pkey = nodeParentKey(groupTargets[0], idSet);
        const siblings = all.filter((n) => nodeParentKey(n, idSet) === pkey);
        const maxZ = Math.max(...siblings.map(nodeStackingZ), 0);
        const sorted = [...groupTargets].sort(compareCanvasSiblings);
        sorted.forEach((node, i) => {
          updateNodeZIndex(node.id, maxZ + 1 + i);
        });
      }
    },
    [canEdit, reactFlowInstance, updateNodeZIndex],
  );

  const handleNodeSendToBack = useCallback(
    (anchorNodeId: string) => {
      if (!canEdit) return;
      const all = reactFlowInstance.getNodes();
      const idSet = new Set(all.map((n) => n.id));
      const targets = zOrderTargetsForContextMenu(all, anchorNodeId);
      if (targets.length === 0) return;

      const byParent = groupNodesByParentKey(targets, idSet);
      for (const groupTargets of byParent.values()) {
        const pkey = nodeParentKey(groupTargets[0], idSet);
        const siblings = all.filter((n) => nodeParentKey(n, idSet) === pkey);
        if (siblings.length < 2) continue;

        const targetIds = new Set(groupTargets.map((n) => n.id));
        const nonTargets = siblings.filter((n) => !targetIds.has(n.id));
        if (nonTargets.length === 0) continue;

        const sortedSiblings = [...siblings].sort(compareCanvasSiblings);
        const k = groupTargets.length;
        const alreadyAtBack =
          sortedSiblings.length >= k &&
          sortedSiblings.slice(0, k).every((n) => targetIds.has(n.id)) &&
          sortedSiblings.slice(k).every((n) => !targetIds.has(n.id));
        if (alreadyAtBack) continue;

        const M = Math.min(...nonTargets.map(nodeStackingZ), 0);
        const sortedTargets = [...groupTargets].sort(compareCanvasSiblings);
        sortedTargets.forEach((node, i) => {
          updateNodeZIndex(node.id, M - k + i);
        });
      }
    },
    [canEdit, reactFlowInstance, updateNodeZIndex],
  );

  return { handleNodeBringToFront, handleNodeSendToBack };
}
